// @flow
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMount } from 'react-use';

import Typography from '@material-ui/core/Typography';
import LockIcon from '@material-ui/icons/LockOutlined';
import CheckIcon from '@material-ui/icons/Done';
import CircularProgress from '@material-ui/core/CircularProgress';

import {
  refreshAccountsMfaFormSubmit,
  refreshAccountsMfaNotProvided,
} from 'data/accounts/actions';
import { getAccountsForInstitutionLoginId } from 'data/accounts/selectors';
import {
  getInstitutionLogins as loadInstitutionLogins,
  refreshAccountsCredentialsProvided,
  refreshAccountsCredentialsNotProvided,
} from 'data/institutionLogins/actions';
import { isInstitutionLoginAmbiguous } from 'data/institutionLogins/selectors';

import { bindPromiseAction } from 'utils/actionHelpers';

import * as actions from '../actions';
import InstitutionLoginCredentialsForm from '../InstitutionLoginCredentialsForm';
import MfaChallengeForm from '../MfaChallengeForm';
import * as selectors from '../selectors';

import { useStyles } from './styles';

type Props = {
  cpSetupMode: string,
  fullAddAccountsDialog: boolean,
  provideForRefreshAccounts: boolean,
  trackingProperties: Object,

  onCancel: () => void,
}

const Authenticator = forwardRef((props: Props, ref) => {
  const { cpSetupMode, fullAddAccountsDialog, onCancel, provideForRefreshAccounts, trackingProperties } = props;
  const dispatch = useDispatch();

  const [page, setPage] = useState(cpSetupMode !== 'PROVIDE-MFA' ? 'CREDENTIALS' : 'MFA');

  const error = useSelector(selectors.getError);
  const institution = useSelector(selectors.getInstitution);
  const institutionLogin = useSelector(selectors.getInstitutionLogin);
  const isPolling = useSelector(selectors.getIsPolling);
  const isSubmitting = useSelector(selectors.getIsSubmitting);
  const mfaChallenge = useSelector(selectors.getMfaChallenge);

  const classes = useStyles();

  // these will only exist when we are updating an existing institution-login
  //
  const institutionLoginId = institutionLogin ? institutionLogin.id : null;
  const institutionLoginIsAmbigous = useSelector((state) => isInstitutionLoginAmbiguous(state, { institutionLoginId }));
  const accounts = useSelector((state) => getAccountsForInstitutionLoginId(state, { institutionLoginId }));

  useMount(() => {
    // lets make sure we have the latest institution-logins (useful for ambiguous message)
    dispatch(loadInstitutionLogins());
  });

  useImperativeHandle(ref, () => ({
    handleClose: (dEvent) => {
      if (provideForRefreshAccounts && dEvent !== 'SUBMIT_SUCCESS') {
        if (page === 'CREDENTIALS') {
          dispatch(refreshAccountsCredentialsNotProvided(institutionLogin ? institutionLogin.id : '0'));
        } else if (page === 'MFA') {
          dispatch(refreshAccountsMfaNotProvided(mfaChallenge ? mfaChallenge.institutionLoginId : '0'));
        }
      }
    },
  }));

  if (mfaChallenge && page !== 'MFA') {
    setPage('MFA');
  }

  const handleCredentialsSubmit = (institutionLoginWithCredentials, resolve, reject) => {
    if (provideForRefreshAccounts) {
      dispatch(refreshAccountsCredentialsProvided(institutionLoginWithCredentials));
      resolve();
    } else {
      const accountDiscoveryCredentialsFormSubmitPromise = bindPromiseAction(dispatch, actions.accountDiscoveryCredentialsFormSubmit);
      accountDiscoveryCredentialsFormSubmitPromise(institutionLoginWithCredentials.set('cpSetupMode', cpSetupMode)).then(
        resolve,
        reject,
      );
    }
  };

  const handleMFASubmit = (mfaResponse, resolve, reject) => {
    if (provideForRefreshAccounts) {
      const refreshAccountsMfaFormSubmitPromise = bindPromiseAction(dispatch, refreshAccountsMfaFormSubmit);
      refreshAccountsMfaFormSubmitPromise(mfaResponse).then(
        () => {
          resolve();
        },
        reject,
      );
    } else {
      const accountDiscoveryMfaFormSubmitPromise = bindPromiseAction(dispatch, actions.accountDiscoveryMfaFormSubmit);
      accountDiscoveryMfaFormSubmitPromise(mfaResponse)
        .then(() => {
          setPage('CREDENTIALS');
          resolve();
        })
        .catch(() => {
          setPage('CREDENTIALS');
          reject();  // TODO:Should we handle error on Mfa Form
        });
    }
  };

  const isBusy = isSubmitting || isPolling;
  return (
    <>
      {!fullAddAccountsDialog && institutionLoginIsAmbigous &&
        <div className={classes.accountsTopBar}>
          <Typography variant="caption" className={classes.accountUpdateText}>
            {`Associated accounts :${accounts && accounts.map((account) => ` ${account && account.name}`).toArray()}`}
          </Typography>
        </div>
      }
      <div className={classes.dialogRoot}>
        <div className={fullAddAccountsDialog ? classes.leftColumn : classes.oneColumn}>
          {isBusy &&
            <div className={classes.loadingRoot}>
              <CircularProgress size={130} className={classes.progress}/>
              <div className={classes.loadingLine}>
                <CheckIcon className={classes.check}/>
                <Typography>
                  Securing Connection...
                </Typography>
              </div>
            </div>
          }
          <div style = {{ display: isBusy ? 'none' : null }}>
            { page === 'CREDENTIALS' &&
              <InstitutionLoginCredentialsForm
                institution={institution}
                institutionLogin={institutionLogin}
                error={error}
                onCancel={onCancel}
                onSubmit={handleCredentialsSubmit}
                cancelButtonLabel={fullAddAccountsDialog ? 'Go Back' : 'Cancel'}
                submitButtonLabel={fullAddAccountsDialog ? 'Connect Bank' : 'Submit'}
                trackingProperties={trackingProperties}
              />
            }
            {page === 'MFA' &&
              <MfaChallengeForm
                institutionLogin={institutionLogin}
                isSubmitting={isSubmitting}
                mfaChallenge={mfaChallenge}
                onCancel={onCancel}
                onSubmit={handleMFASubmit}
                cancelButtonLabel={fullAddAccountsDialog ? 'Back' : 'Cancel'}
                submitButtonLabel={fullAddAccountsDialog ? 'Continue' : 'Submit'}
                trackingProperties={trackingProperties}
              />
            }
          </div>
        </div>
        {fullAddAccountsDialog &&
          <div className={classes.rightColumn}>
            <div className={classes.border}>
              <LockIcon className={classes.bigLock}/>
              <Typography className={classes.connectPara}>
                Secured, encrypted connections.
              </Typography>
              <br/>
              <a href={institution.ofx.contact_url} target="_blank" className={classes.noUnder}>
                <Typography variant="body2" className={classes.connectPara}>
                  {institution.ofx.contact_url}
                </Typography>
              </a>
            </div>
          </div>
        }
      </div>
    </>
  );
});

export default Authenticator;
